import {html} from 'lit';
import Base from '../../base.js';
import '../../icon/icon.js';
import './button-fab-item.js';
import styles from './button-fab.css' assert {type: 'css'};

/**
 * @element inv-button-fab
 *
 * @class ButtonFab
 * @classdesc This is a web component that creates a floating action button (FAB).
 *
 * @property {Array} [items=[]] - Array of objects with properties for each item {icon, href, background}.
 *
 * @cssproperty {string} --inv-button-fab-background - The background gradient for the FAB in its normal state.
 * @default linear-gradient(93.64deg, #00d5e0 1%, #15a6da 26.85%, #004bba 86.58%, #0f4595 99.6%)
 *
 * @cssproperty {string} --inv-button-fab-hover-background - The background color for the FAB in its hover state.
 * @default #0e4dab
 *
 * @cssproperty {string} --inv-button-fab-item-icon-color - The color of the icon.
 * @default #AABACA
 */

export default class ButtonFab extends Base {
    static get properties() {
        return {
            items: {
                type: Array,
            },
        };
    }

    constructor() {
        super();
        this.show = false;
        this.items = [{icon: 'info', href: '/', background: '#ffa500'}];
    }

    getStyleSheets() {
        return [...super.getStyleSheets(), styles];
    }

    get #fabMenuId() {
        return 'fab-menu-' + this.getId();
    }

    get #iconContainerId() {
        return 'icon-container-' + this.getId();
    }

    get #iconContainer() {
        return this._getElementById(this.#iconContainerId);
    }

    get #fabMenu() {
        return this._getElementById(this.#fabMenuId);
    }

    onButtonClick = () => {
        if (this.show) {
            this.show = false;
            this.removeClasses(this.#iconContainerId, 'show');
            this.removeClasses(this.#fabMenuId, 'show');
        } else {
            this.show = true;
            this.addClasses(this.#iconContainerId, 'show');
            this.addClasses(this.#fabMenuId, 'show');
        }
    };

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.onLoaFabItems();
    }

    onLoaFabItems() {
        const fabMenu = this._getElementById(this.#fabMenuId);
        if (fabMenu && this.items) {
            this.appendChildren(
                this.items.map((option) => {
                    const item = document.createElement('inv-button-fab-item');
                    item.icon = option.icon;
                    item.href = option.href;
                    item.label = option.label ?? null;
                    item.target = option.target ?? '_self';
                    item.rel = option.rel ?? 'noopener noreferrer';
                    if (option.background) {
                        item.style.setProperty('--inv-button-fab-item-background', option.background);
                    }
                    return item;
                }),
                fabMenu
            );
        }
    }

    render() {
        return html`
            <div class="fab-container">
                <ul tabindex="-1" id="${this.#fabMenuId}" class="fab-menu"></ul>
                <div class="button-container">
                    <button type="button" class="icon-button" @click="${this.buildHandler('button-click')}">
                        <div id="${this.#iconContainerId}" class="icon-container">
                            <inv-icon
                                color="#fff"
                                width="2.2em"
                                height="2.2em"
                                color="var(--inv-button-fab-icon-color,#AABACA)"
                                name="plus"
                            ></inv-icon>
                        </div>
                    </button>
                </div>
            </div>
        `;
    }
}

ButtonFab.register();
